import { ApiError } from '@limuzin/kit';

/**
 * «Согласие» ticked and not yet taken by the server (ADR 0030) — the twin of
 * `ConsentOutbox` in `limuzin_core`.
 *
 * Ticking the box is a record: the passenger consented whether or not the server
 * heard at once. So the screen lets them through the moment it is ticked, and this
 * sends it. It carries when it was ticked, and the server writes it then; sent twice,
 * the server keeps the first; there is one per account, so no order to keep; and the
 * screen goes at once. A refusal brings the screen back: the text was replaced
 * meanwhile, and the new one is the one to tick. Kept in `localStorage`, so a reload
 * before the signal returns does not lose it.
 */

/**
 * Whether «Регистрация» shows the box and the documents under it. Off while the
 * pages are drafts (docs/legal/notes.md), so nobody is asked to agree to
 * them. It goes on with CONSENT_ASKED on the server, which asks for «Согласие».
 */
export const legalDocumentsLive = true;

export interface HeldConsent {
  /** Whose consent it is: one account's is never sent in another's name. */
  userId: string;
  /** The редакция the box was ticked on — `consentDue` as it stood then. */
  edition: string;
  /** When it was ticked, as ISO-8601. */
  at: string;
}

export type ConsentGiven =
  | { kind: 'sent' }
  | { kind: 'kept' }
  | { kind: 'refused'; code: string; message: string };

/** What sends one: `sendConsent`, in the app. */
export type ConsentDelivery = (consent: HeldConsent) => Promise<unknown>;

const KEY = 'limuzin.customer.held-consent';

/** How long one that could not be sent is worth keeping; past it the screen asks again. */
const KEEP_FOR = 30 * 24 * 60 * 60 * 1000;

const RETRY_MS = 30_000;
let retry: ReturnType<typeof setTimeout> | null = null;

/** What the server took this tab: until the profile is asked for again it still says due. */
const taken = new Set<string>();
const name = (userId: string, edition: string) => `${userId}\u0000${edition}`;

function read(): HeldConsent[] {
  try {
    const stored = localStorage.getItem(KEY);
    if (!stored) return [];
    const since = Date.now() - KEEP_FOR;
    return (JSON.parse(stored) as HeldConsent[]).filter(
      (c) => typeof c?.userId === 'string' && Date.parse(c.at) > since,
    );
  } catch {
    return [];
  }
}

function write(held: HeldConsent[]): void {
  try {
    if (held.length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(held));
  } catch {
    // Held for as long as this tab is open, and no longer.
  }
}

function drop(consent: HeldConsent): void {
  write(read().filter((c) => !(c.userId === consent.userId && c.edition === consent.edition)));
}

/** Whether `userId` ticked the box on `edition` here — waiting, or taken this tab. */
export function holdsConsent(userId: string, edition: string): boolean {
  return (
    taken.has(name(userId, edition))
    || read().some((c) => c.userId === userId && c.edition === edition)
  );
}

async function send(consent: HeldConsent, deliver: ConsentDelivery): Promise<ConsentGiven> {
  try {
    await deliver(consent);
  } catch (problem) {
    if (problem instanceof ApiError && problem.code === 'offline') {
      if (retry === null) {
        retry = setTimeout(() => {
          retry = null;
          void flushConsent(consent.userId, deliver);
        }, RETRY_MS);
      }
      return { kind: 'kept' };
    }
    drop(consent);
    return problem instanceof ApiError
      ? { kind: 'refused', code: problem.code, message: problem.message }
      : { kind: 'refused', code: 'unknown', message: 'Что-то пошло не так' };
  }
  taken.add(name(consent.userId, consent.edition));
  drop(consent);
  return { kind: 'sent' };
}

/** The box ticked now: kept, and sent at once. */
export function giveConsent(
  userId: string,
  edition: string,
  deliver: ConsentDelivery,
): Promise<ConsentGiven> {
  const consent: HeldConsent = { userId, edition, at: new Date().toISOString() };
  write([...read().filter((c) => c.userId !== userId), consent]);
  return send(consent, deliver);
}

/** Sends what `userId` has waiting. */
export async function flushConsent(userId: string | null, deliver: ConsentDelivery): Promise<void> {
  if (!userId) return;
  for (const consent of read()) {
    if (consent.userId === userId) await send(consent, deliver);
  }
}

/** Lets go of everything: the passenger signed out, and is asked again on the way in. */
export function clearConsent(): void {
  if (retry !== null) clearTimeout(retry);
  retry = null;
  taken.clear();
  write([]);
}
